import { useState, useCallback } from "react";
import { toast } from "sonner";
import { uploadFile } from "@/network/network";
import type { Location } from "@/bryntum-lib/models/Location";
import { useEntityFiles } from "@/react/components/entityViews/files/api/files.api";
import { createFile, deleteFileById } from "@/react/components/entityViews/files/api/files.service";
import { fileKeys } from "@/react/components/entityViews/files/api/files.query-keys";
import { queryClient } from "@/lib/queryClient";

export const filterLocationPhotoFiles = (files: FileList | File[]) =>
  Array.from(files).filter((file) => file.type.startsWith("image/"));

export const useLocationPhotos = (locationRecord: Location | null | undefined, boardId: number) => {
  const [isUploading, setIsUploading] = useState(false);

  const { files: photos } = useEntityFiles({
    boardId,
    entityType: "location",
    entityId: locationRecord?.id as number,
    enabled: !!locationRecord?.id && !!boardId,
  });

  const uploadPhotos = useCallback(
    async (uploadedFiles: FileList | File[]) => {
      if (!locationRecord?.id) return;
      const uploadedFileArray = Array.from(uploadedFiles);
      const fileArray = filterLocationPhotoFiles(uploadedFileArray);
      const rejectedCount = uploadedFileArray.length - fileArray.length;

      if (rejectedCount > 0) {
        toast.error("Only image files can be uploaded as location photos.");
      }
      if (fileArray.length === 0) return;

      const fileCount = fileArray.length;
      const fileText = fileCount === 1 ? "photo" : "photos";

      setIsUploading(true);
      try {
        toast.loading(`Uploading ${fileCount} ${fileText}...`, { id: "location-photo-upload" });

        const uploadPromises = fileArray.map(async (file) => {
          const { data: uploadData } = await uploadFile(file, file.name);
          return uploadData.id;
        });

        const documentIds = await Promise.all(uploadPromises);

        await Promise.all(
          documentIds.map((id) =>
            createFile({
              document: id,
              board_id: boardId,
              location: locationRecord.id as number,
            })
          )
        );
        queryClient.invalidateQueries({
          queryKey: fileKeys.entity(boardId, "location", locationRecord.id as number),
        });

        toast.success(`Successfully uploaded ${fileCount} ${fileText}`, { id: "location-photo-upload" });
      } catch (error) {
        console.error("Location photo upload error:", error);
        toast.error(`Failed to upload ${fileText}. Please try again.`, { id: "location-photo-upload" });
      } finally {
        setIsUploading(false);
      }
    },
    [locationRecord?.id, boardId]
  );

  const removePhoto = useCallback(
    async (id: string | number) => {
      try {
        await deleteFileById(Number(id));
        queryClient.invalidateQueries({
          queryKey: fileKeys.entity(boardId, "location", locationRecord?.id as number),
        });
        toast.success("Photo removed");
      } catch (error) {
        console.error("Location photo remove error:", error);
        toast.error("Failed to remove photo. Please try again.");
      }
    },
    [boardId, locationRecord?.id]
  );

  return { photos, isUploading, uploadPhotos, removePhoto };
};
